import { useMapContext } from '@components/map/MapContext';
import { DEFAULT_BOUNDS } from '@utils/map/map.constants';
import { BBox, GeoJsonProperties } from 'geojson';
import { isEqual } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import Supercluster, { PointFeature } from 'supercluster';
import useDeepCompareEffect from 'use-deep-compare-effect';

interface UseSuperclusterArgs<P, C> {
  points: Supercluster.PointFeature<P>[];
  options?: Supercluster.Options<P, C>;
}

interface UseSuperclusterReturn {
  clusters: PointFeature<GeoJsonProperties>[];
  leaveIds: string[];
  supercluster: Supercluster<GeoJsonProperties, GeoJsonProperties> | null;
}

const useSupercluster = <
  P extends GeoJsonProperties = Supercluster.AnyProps,
  C extends GeoJsonProperties = Supercluster.AnyProps,
>({
  points,
  options,
}: UseSuperclusterArgs<P, C>): UseSuperclusterReturn => {
  const { current: currentMap } = useMap();
  const { viewState } = useMapContext();
  const memoizedOptions = useMemo(() => options, [options]);
  const superclusterRef = useRef<Supercluster<P, C> | null>(new Supercluster(memoizedOptions));
  const pointsRef = useRef<Supercluster.PointFeature<P>[]>(null);
  const [clusters, setClusters] = useState<PointFeature<GeoJsonProperties>[]>([]);

  const bounds = currentMap ? (currentMap.getBounds()?.toArray().flat() as BBox) : DEFAULT_BOUNDS;

  useDeepCompareEffect(() => {
    if (superclusterRef.current && !isEqual(pointsRef.current, points)) {
      superclusterRef.current.load(points);
      pointsRef.current = points;
    }

    if (superclusterRef.current) {
      const clusters = superclusterRef.current.getClusters(bounds, viewState.zoom);
      setClusters(clusters);
    }
  }, [points, bounds, viewState.zoom]);

  const leaveIds = clusters.flatMap((cluster) => {
    if (cluster.properties?.cluster && superclusterRef.current) {
      return superclusterRef.current
        .getLeaves(cluster.properties.cluster_id)
        .map((leaf) => leaf.properties?.id)
        .filter(Boolean);
    }
    return [];
  });

  return { clusters, leaveIds: leaveIds, supercluster: superclusterRef.current };
};

export default useSupercluster;
